import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Calendar, Clock, UserCheck, UserX, Users, Search,
  Globe, AlertCircle, Coffee, TrendingUp, Download,
  RefreshCcw, LogIn, LogOut, Filter, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatTimeFromISO } from '@/utils/timezoneUtils';
import { useLanguage } from '@/i18n/LanguageContext';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmtMins = (mins) => {
  if (!mins || mins <= 0) return '—';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const STATUS_STYLES = {
  present: 'bg-green-100 text-green-700',
  late:    'bg-amber-100 text-amber-700',
  absent:  'bg-red-100 text-red-700',
  day_off: 'bg-blue-100 text-blue-700',
};

// ─── component ──────────────────────────────────────────────────────────────
export default function ManagerReportsPage() {
  const { t } = useLanguage();

  // filters
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [search, setSearch] = useState('');

  // data
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (selectedStaff !== 'all') params.staff_id = selectedStaff;
      const res = await axios.get(`${API}/manager/attendance/history`, { params, withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load attendance history');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedStaff]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // quick range presets
  const applyRange = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (days - 1));
    setDateTo(to.toISOString().split('T')[0]);
    setDateFrom(from.toISOString().split('T')[0]);
  };

  const records = data?.records || [];
  const filtered = records.filter(r =>
    !search || r.staff_name?.toLowerCase().includes(search.toLowerCase())
  );

  const summary = data?.summary || {};
  const staffList = data?.staff_list || [];
  const timezone = data?.timezone || 'UTC';

  const statCards = [
    { label: 'Total Records', value: summary.total ?? 0, icon: Calendar, cls: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
    { label: 'Present', value: summary.present ?? 0, icon: UserCheck, cls: 'text-green-600 bg-green-50 border-green-200' },
    { label: 'Late', value: summary.late ?? 0, icon: Clock, cls: 'text-amber-600 bg-amber-50 border-amber-200' },
    { label: 'Absent', value: summary.absent ?? 0, icon: UserX, cls: 'text-red-600 bg-red-50 border-red-200' },
    { label: 'Total Work', value: fmtMins(summary.total_work_minutes), icon: TrendingUp, cls: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
    { label: 'Total Break', value: fmtMins(summary.total_break_minutes), icon: Coffee, cls: 'text-orange-600 bg-orange-50 border-orange-200' },
  ];

  return (
    <div className="space-y-6" data-testid="manager-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Attendance History</h2>
          <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
            Your team's full attendance log
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              <Globe className="w-3 h-3" />{timezone}
            </span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchHistory} className="gap-2" data-testid="refresh-btn">
          <RefreshCcw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Filter bar */}
      <Card className="shadow-sm" data-testid="filter-bar">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-end">
            {/* Staff selector */}
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-gray-500 font-medium mb-1 block">Staff Member</label>
              <select
                value={selectedStaff}
                onChange={e => setSelectedStaff(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                data-testid="staff-filter"
              >
                <option value="all">All Team Staff</option>
                {staffList.map(s => (
                  <option key={s.staff_id} value={s.staff_id}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* Date from */}
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1 block">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                data-testid="date-from"
              />
            </div>

            {/* Date to */}
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1 block">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                data-testid="date-to"
              />
            </div>

            {/* Quick ranges */}
            <div className="flex gap-2">
              {[
                { label: '7d', days: 7 },
                { label: '30d', days: 30 },
                { label: '90d', days: 90 },
              ].map(r => (
                <button
                  key={r.label}
                  onClick={() => applyRange(r.days)}
                  className="px-3 py-2 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-emerald-400 transition-all"
                  data-testid={`range-${r.label}`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {/* Clear staff filter */}
            {selectedStaff !== 'all' && (
              <button
                onClick={() => setSelectedStaff('all')}
                className="flex items-center gap-1 px-3 py-2 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                data-testid="clear-filter-btn"
              >
                <X className="w-3 h-3" /> Clear Filter
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {statCards.map(c => (
              <Card key={c.label} className={`border-2 ${c.cls.split(' ').find(x => x.startsWith('border'))} shadow-sm`}
                data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex flex-col items-center text-center gap-2">
                    <div className={`p-2 rounded-xl ${c.cls.split(' ').find(x => x.startsWith('bg'))}`}>
                      <c.icon className={`w-4 h-4 ${c.cls.split(' ').find(x => x.startsWith('text'))}`} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{c.value}</p>
                      <p className="text-xs text-gray-500 font-medium leading-tight">{c.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Table */}
          <Card className="shadow-lg" data-testid="history-table">
            <CardHeader className="border-b bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-600" />
                  Records
                  <span className="text-sm font-normal text-gray-500">
                    ({dateFrom} — {dateTo})
                  </span>
                </CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-10 w-56"
                    data-testid="search-input"
                  />
                </div>
              </div>
              {search && (
                <p className="text-xs text-gray-400 mt-1">
                  Showing {filtered.length} of {records.length} records
                </p>
              )}
            </CardHeader>

            <CardContent className="p-0">
              {filtered.length === 0 ? (
                <div className="text-center py-14 text-gray-400" data-testid="no-records">
                  <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No records found</p>
                  <p className="text-sm mt-1">Try adjusting the date range or filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">Date</TableHead>
                        {selectedStaff === 'all' && <TableHead className="font-semibold">Staff</TableHead>}
                        <TableHead className="font-semibold">Status</TableHead>
                        <TableHead className="font-semibold">Check In</TableHead>
                        <TableHead className="font-semibold">Check Out</TableHead>
                        <TableHead className="font-semibold">Work Hours</TableHead>
                        <TableHead className="font-semibold">Break</TableHead>
                        <TableHead className="font-semibold">Late By</TableHead>
                        <TableHead className="font-semibold">Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((rec, idx) => (
                        <TableRow
                          key={`${rec.staff_id}_${rec.date}_${idx}`}
                          data-testid={`row-${idx}`}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          {/* Date */}
                          <TableCell className="font-medium text-gray-900 whitespace-nowrap">
                            {new Date(rec.date + 'T12:00:00').toLocaleDateString('en-US', {
                              weekday: 'short', month: 'short', day: 'numeric'
                            })}
                          </TableCell>

                          {/* Staff name (only when showing all) */}
                          {selectedStaff === 'all' && (
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-emerald-700 font-semibold text-xs">
                                    {rec.staff_name?.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{rec.staff_name}</p>
                                  {rec.department && (
                                    <p className="text-xs text-gray-400">{rec.department}</p>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          )}

                          {/* Status */}
                          <TableCell>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[rec.status] || 'bg-gray-100 text-gray-600'}`}>
                              {rec.status === 'present' ? 'Present'
                                : rec.status === 'late' ? 'Late'
                                : rec.status === 'absent' ? 'Absent'
                                : rec.status === 'day_off' ? 'Day Off'
                                : rec.status || 'Not Marked'}
                            </span>
                          </TableCell>

                          {/* Check In */}
                          <TableCell>
                            {rec.check_in
                              ? <span className="text-green-700 font-medium text-sm">{formatTimeFromISO(rec.check_in)}</span>
                              : <span className="text-gray-300">—</span>}
                          </TableCell>

                          {/* Check Out */}
                          <TableCell>
                            {rec.check_out
                              ? <span className={`font-medium text-sm ${rec.force_checkout ? 'text-red-600' : 'text-blue-700'}`}>
                                  {formatTimeFromISO(rec.check_out)}
                                  {rec.force_checkout && <span className="ml-1 text-xs">(F)</span>}
                                </span>
                              : <span className="text-gray-300">—</span>}
                          </TableCell>

                          {/* Work Hours */}
                          <TableCell>
                            {rec.total_work_minutes > 0
                              ? <span className="font-semibold text-emerald-700 text-sm">{fmtMins(rec.total_work_minutes)}</span>
                              : <span className="text-gray-300">—</span>}
                          </TableCell>

                          {/* Break */}
                          <TableCell>
                            {rec.total_break_minutes > 0 ? (
                              <span className="text-orange-600 text-sm">
                                {fmtMins(rec.total_break_minutes)}
                                {rec.breaks_count > 0 && (
                                  <span className="text-xs text-gray-400 ml-1">({rec.breaks_count}x)</span>
                                )}
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </TableCell>

                          {/* Late By */}
                          <TableCell>
                            {rec.late_by > 0
                              ? <span className="text-amber-600 font-semibold text-sm">{rec.late_by} min</span>
                              : <span className="text-gray-300">—</span>}
                          </TableCell>

                          {/* Notes */}
                          <TableCell>
                            {rec.force_checkout && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                                <AlertCircle className="w-3 h-3" /> Force Out
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
